import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, GraduationCap, FileSpreadsheet, ScanLine, Target, QrCode, Clock, PlusCircle, TrendingUp } from "lucide-react";
import StudentOfWeek from "@/components/StudentOfWeek";
import ExamTypeToggle from "@/components/ExamTypeToggle";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, sub, tid }) => (
  <Card className="p-4 bg-card border-border" data-testid={tid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-2xl font-bold mt-1.5 tracking-tight">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </div>
      <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center"><Icon className="h-5 w-5" /></div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [examType, setExamType] = useState("KTT");
  const typeLabel = examType === "KTT" ? "KTT" : "Deneme";
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard", { params: { exam_type: examType } }).then((r) => setD(r.data)).catch(() => {}); }, [examType]);
  if (!d) return <div className="text-muted-foreground">Yükleniyor...</div>;

  return (
    <div className="overflow-x-hidden">
      <PageHeader title="Genel Bakış" desc="Okul geneli sınav performans özeti">
        <ExamTypeToggle value={examType} onChange={setExamType} tid="dash-type" />
        <Button onClick={() => nav("/sinav-olustur")} data-testid="dash-new-exam"><PlusCircle className="h-4 w-4 mr-2" />Yeni {typeLabel} Başlat</Button>
        <Button variant="outline" onClick={() => nav("/optik-okuma")} data-testid="dash-read"><ScanLine className="h-4 w-4 mr-2" />Optik Oku</Button>
      </PageHeader>

      <StudentOfWeek compact examType={examType} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={Users} label="Toplam Öğrenci" value={d.total_students} tid="stat-students" />
        <StatCard icon={GraduationCap} label="Sınıf / Şube" value={d.total_classes} tid="stat-classes" />
        <StatCard icon={FileSpreadsheet} label="Yapılan Sınav" value={d.total_exams} tid="stat-exams" />
        <StatCard icon={Target} label="Ortalama Puan" value={d.avg_success} sub="500 üzerinden" tid="stat-avg" />
        <StatCard icon={Clock} label={`Son ${typeLabel}`} value={d.last_exam?.name || "—"} sub={d.last_exam?.date} tid="stat-last" />
        <StatCard icon={QrCode} label="Okunan Optik" value={d.optics_read} tid="stat-read" />
        <StatCard icon={ScanLine} label="Bekleyen Optik" value={d.pending_optics} tid="stat-pending" />
        <StatCard icon={TrendingUp} label="Toplam Sonuç" value={d.optics_read} tid="stat-results" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <Card className="p-5 bg-card border-border min-w-0 overflow-hidden">
          <h3 className="font-semibold mb-4">Son {typeLabel} — Ders Bazlı Net Ortalaması</h3>
          {d.last_subject_perf?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={d.last_subject_perf}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
                <Bar dataKey="net" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} name="Net" />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-muted-foreground py-16 text-center">Henüz sonuç yok.</div>}
        </Card>

        <Card className="p-5 bg-card border-border min-w-0 overflow-hidden">
          <h3 className="font-semibold mb-4">Sınav Net Trendi</h3>
          {d.trend?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={d.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Legend />
                <Line type="monotone" dataKey="net" stroke="hsl(var(--primary))" strokeWidth={2.5} name="Ort. Net" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="score" stroke="hsl(var(--accent))" strokeWidth={2} name="Ort. Puan" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-muted-foreground py-16 text-center">Henüz sonuç yok.</div>}
        </Card>
      </div>
    </div>
  );
}
